import React, { useEffect } from 'react';

import Grid from '@mui/material/Grid';

import EvolutionDateFilters from '../../../../components/EvolutionDateFilters';
import FormHandleControl from '../../../../components/FormHandleControl';
import FormProductTypeSelect from '../../../../components/FormProductTypeSelect';
import FormRegistrationTypeSelect from '../../../../components/FormRegistrationTypeSelect';
import FormSchoolLevelSelect from '../../../../components/FormSchoolLevelSelect';
import FormSchoolSelect from '../../../../components/FormSchoolSelect';
import PaperWrapper from '../../../../components/PaperWrapper';
import { useFetchDefaultFiltersData } from '../../../../hooks/useFetchDefaultFiltersData';
import { useFetchStaffEvolutionFiltersData } from '../../../../hooks/useFetchStaffEvolutionFiltersData';
import { useFilters } from '../../../../hooks/useFilters';
import { useIsValidEvolutionFilters } from '../../../../hooks/useIsValidEvolutionFilters';
import { IFilterList } from '../../../../types/filter-list.type';
import { IFilters } from '../../types';

const StaffEvolutionFilters = React.memo(
  ({ onSubmit, onExport, isSubmitProcess, isExportProcess }: IFilterList<IFilters>) => {
    const { schools, schoolLevels, schoolYears, isLoading } = useFetchStaffEvolutionFiltersData();

    const defaultFilters = useFetchDefaultFiltersData<IFilters>('staffEvolution');

    const {
      filters,
      setFilters,
      handleSelectChange,
      handleDateChange,
      handleMultipleChoiceChange,
    } = useFilters<IFilters>(defaultFilters);

    const isValid = useIsValidEvolutionFilters(filters);

    useEffect(() => {
      if (
        isLoading ||
        filters.period1SchoolYear?.length ||
        filters.period2SchoolYear?.length ||
        !schoolYears.length
      ) {
        return;
      }

      setFilters({
        ...filters,
        period1SchoolYear: schoolYears[schoolYears.length - 1].code,
        period2SchoolYear: schoolYears[schoolYears.length - 2].code,
      });
    }, [filters, isLoading, schoolYears, setFilters]);

    const handleSubmit = (event: React.FormEvent) => {
      event.preventDefault();

      if (isLoading) {
        return;
      }

      onSubmit(filters);
    };

    return (
      <PaperWrapper isLoading={isLoading}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={1} direction="row" alignItems="center">
            <EvolutionDateFilters
              filters={filters}
              schoolYears={schoolYears}
              handleDateChange={handleDateChange}
              handleSelectChange={handleSelectChange}
            />
          </Grid>

          <Grid container spacing={1} direction="row" alignItems="flex-start">
            <FormSchoolLevelSelect
              value={filters.schoolLevel}
              schoolLevels={schoolLevels}
              onChange={(value) => handleMultipleChoiceChange('schoolLevel', value)}
            />

            <FormSchoolSelect
              onChange={handleSelectChange}
              schools={schools}
              value={filters.school}
            />

            <FormProductTypeSelect value={filters.productType} onChange={handleSelectChange} />

            <FormRegistrationTypeSelect
              value={filters.registrationType}
              onChange={handleSelectChange}
            />
          </Grid>

          <FormHandleControl
            isSubmitProcess={isSubmitProcess}
            isExportProcess={isExportProcess}
            isDisabled={!isValid}
            onExport={() => onExport(filters)}
          />
        </form>
      </PaperWrapper>
    );
  },
);

export default StaffEvolutionFilters;
